import { faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AUTHORITIES, PERMISSIONS } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority, hasPermissions } from 'app/shared/auth/private-route';
import { readFileContent } from 'app/shared/util/file-utils';
import { reactStrapStyles } from 'app/shared/util/form-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { Translate, ValidatedField } from 'react-jhipster';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Button, Col, FormFeedback, Input, InputGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { getEntity, updateEntity, updateMeshCommand } from '../device.reducer';
import { mapDeviceEntityToFormValues, mapToMeshPayload } from '../utils';

export const masterOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];
export const sprinklerOptions = [
  { value: 'ON', label: 'ON' },
  { value: 'OFF', label: 'OFF' },
  { value: 'AUTO', label: 'AUTO' },
];
export const DeviceConfiguration = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<'id'>();

  const deviceEntity = useAppSelector(state => state.device.entity);
  const loading = useAppSelector(state => state.device.loading);

  const getLatestEntity = () => {
    dispatch(getEntity(id));
  };

  useEffect(() => {
    getLatestEntity();
  }, []);

  const saveEntity = async values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    const firmwareBlob = await readFileContent(values.file?.[0]);
    const firmware = {
      path: values.file?.[0]?.name,
      fileType: values.file?.[0]?.name,
      fileObject: firmwareBlob,
      fileObjectContentType: values.file?.[0]?.type,
    };
    const entity = {
      ...values,
      city: {
        id: values?.city?.id,
      },
      // user: values?.user?.id,
      master: values?.master?.value,
      broadcastSSID: values?.broadcastSSID?.value,
      sprinklerStatus: values?.sprinkler?.value,
      firmware,
      requestFromClientApp: true,
    };
    delete entity.file;
    delete entity.masterVal;
    if (deviceEntity.master !== values?.master?.value || deviceEntity?.sprinklerStatus !== values?.sprinkler?.value) {
      const meshPayload = mapToMeshPayload({ values, deviceEntity });
      dispatch(updateMeshCommand(meshPayload));
    }
    dispatch(updateEntity(entity));
  };
  return <DeviceConfigurationForm saveEntity={saveEntity} deviceEntity={deviceEntity} loading={loading} onRefresh={getLatestEntity} />;
};

export const DeviceConfigurationForm = ({ saveEntity, deviceEntity, loading, onRefresh }) => {
  const dispatch = useAppDispatch();
  const formControl = useForm();
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset: resetForm,
  } = formControl;

  const permissions = useAppSelector(state => state.authentication.account.permissions);
  const isReadOnly = hasPermissions(permissions, [PERMISSIONS.DEVICE_CONFIGURATION_READ]);

  const defaultValues = useMemo(() => {
    return mapDeviceEntityToFormValues({
      deviceEntity,
    });
  }, [deviceEntity]);

  useEffect(() => {
    resetForm({
      ...defaultValues,
    });
  }, [resetForm, defaultValues]);

  const isDeviceOwner = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.DEVICE_OWNER]));

  const [isDeviceOn, setIsDeviceOn] = useState(deviceEntity?.repairModeStatus === 'ON');
  const [showPowerConfirm, setShowPowerConfirm] = useState(false);

  useEffect(() => {
    setIsDeviceOn(deviceEntity?.repairModeStatus === 'ON');
  }, [deviceEntity]);

  const confirmTogglePower = () => {
    const repairModeStatus = isDeviceOn ? 'OFF' : 'ON';
    setIsDeviceOn(!isDeviceOn);
    dispatch(updateEntity({ ...deviceEntity, repairModeStatus, requestFromClientApp: true }));
    setShowPowerConfirm(false);
  };

  return (
    <div className="configuration-tab">
      <div className="d-flex justify-content-end">
        <Button
          color="info"
          className="me-2"
          id="refresh-configuration"
          data-cy="refreshConfigurationButton"
          onClick={onRefresh}
          style={{ pointerEvents: loading ? 'none' : 'all' }}
        >
          <FontAwesomeIcon icon="sync" spin={loading} /> Refresh
        </Button>
      </div>
      <Row className="justify-content-center">
        {/* <div className="mt-10">&nbsp;</div> */}
        <Col md="12">
          <form onSubmit={vals => void handleSubmit(saveEntity)(vals)}>
            <div className="form-container">
              <div className="form-body">
                <div className="d-flex">
                  <ValidatedField
                    register={register}
                    type="text"
                    id="device-name"
                    data-cy="device-name"
                    name="name"
                    error={errors?.name as FieldError}
                    label="Device Name*"
                    disabled={isReadOnly}
                    validate={{
                      required: { value: true, message: 'This field is required' },
                    }}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="serial-no"
                    data-cy="serial-no"
                    name="serialNumber"
                    error={errors?.serialNumber as FieldError}
                    label="Serial No."
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="device-status"
                    data-cy="device-status"
                    name="state"
                    error={errors?.state as FieldError}
                    label="Device Status"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="timezone"
                    data-cy="timezone"
                    name="timezone"
                    error={errors?.timezone as FieldError}
                    label="Timezone"
                    disabled={true}
                  />
                </div>

                <div className="d-flex">
                  <ValidatedField
                    register={register}
                    type="text"
                    id="ipv4-address"
                    data-cy="ipv4-address"
                    name="ipv4"
                    error={errors?.ipv4 as FieldError}
                    label="IPv4 Address"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="ipv6-address"
                    data-cy="ipv6-address"
                    name="ipv6"
                    error={errors?.ipv6 as FieldError}
                    label="IPv6 Address"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="subnet"
                    data-cy="subnet"
                    name="subnet"
                    error={errors?.subnet as FieldError}
                    label="Subnet"
                    disabled={true}
                  />

                  <div style={{ display: 'flex' }} className="flex-column selectable">
                    <Label for="macAddress">{'Mac Address'}</Label>
                    <Controller
                      control={control}
                      name="macAddress"
                      rules={{
                        validate(number: string | null) {
                          if (number && number.length < 12) {
                            return 'Valid Mac Address is required';
                          }
                        },
                      }}
                      render={({ field, fieldState }) => {
                        return (
                          <>
                            <InputMask
                              disabled={isReadOnly}
                              mask="**-**-**-**-**-**"
                              maskChar="_"
                              value={field.value}
                              onChange={e => field.onChange(e)}
                            >
                              {inputProps => (
                                <Input
                                  {...inputProps}
                                  disabled={isReadOnly}
                                  className={`${fieldState?.error ? 'is-invalid' : ''}`}
                                  data-testid="macAddress"
                                  id="mac-address"
                                  placeholder="__-__-__-__-__-__"
                                />
                              )}
                            </InputMask>
                            <FormFeedback>{fieldState?.error && fieldState?.error?.message}</FormFeedback>
                          </>
                        );
                      }}
                    />
                  </div>
                </div>

                <div className="d-flex">
                  <ValidatedField
                    register={register}
                    type="text"
                    id="frequency"
                    data-cy="frequency"
                    name="frequency"
                    error={errors?.frequency as FieldError}
                    label="Frequency"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="hop-depth"
                    data-cy="hop-depth"
                    name="hopDepth"
                    error={errors?.hopDepth as FieldError}
                    label="Hop Depth"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="sprinkler-status"
                    data-cy="sprinkler-status"
                    name="sprinklerStatus"
                    error={errors?.sprinklerStatus as FieldError}
                    label="Sprinkler Status"
                    disabled={true}
                  />

                  <ValidatedField
                    register={register}
                    type="text"
                    id="master"
                    data-cy="master"
                    name="masterVal"
                    value={deviceEntity?.master ? 'Yes' : 'No'}
                    error={errors?.master as FieldError}
                    label="Master"
                    disabled={true}
                  />
                </div>

                <div className="d-flex">
                  <ValidatedField
                    register={register}
                    type="text"
                    id="bill-status"
                    data-cy="bill-status"
                    name="billStatusVal"
                    value={deviceEntity?.billStatus ? 'Yes' : 'No'}
                    label="Subscribed"
                    disabled={true}
                  />
                </div>

                <hr />

                <div className="d-flex" style={{ marginTop: '35px' }}>
                  <ValidatedField
                    register={register}
                    type="text"
                    id="ssid"
                    data-cy="ssid-name"
                    name="ssid"
                    error={errors?.ssid as FieldError}
                    label="SSID Name"
                    disabled={isReadOnly}
                  />

                  <div style={{ display: 'flex' }} className="flex-column">
                    <Label for="broadcastSSID">{'Broadcast SSID'}</Label>
                    <InputGroup
                      className="mb-3"
                      error={errors?.broadcastSSID}
                      id="broadcastSSID"
                      data-testid="sprinkler"
                      data-cy="broadcastSSID"
                    >
                      <Controller
                        control={control}
                        name="broadcastSSID"
                        render={({ field, fieldState }) => {
                          return (
                            <>
                              <Select
                                menuPlacement="auto"
                                classNamePrefix="broadcast"
                                isDisabled={isReadOnly}
                                options={masterOptions}
                                styles={reactStrapStyles({ error: !!fieldState?.error?.message })}
                                value={field.value}
                                onChange={option => field.onChange(option)}
                              />
                              <FormFeedback>{fieldState.error?.message}</FormFeedback>
                            </>
                          );
                        }}
                      />
                    </InputGroup>
                  </div>
                </div>

                <div className="d-flex align-items-center device-power-toggle">
                  <span className="device-power-toggle-label">Technician Bypass Mode</span>
                  <button
                    type="button"
                    id="device-power-toggle"
                    data-cy="device-power-toggle"
                    data-testid="device-power-toggle"
                    className={`power-toggle ${isDeviceOn ? 'is-on' : 'is-off'}`}
                    aria-pressed={isDeviceOn}
                    onClick={() => setShowPowerConfirm(true)}
                  >
                    <span className="power-toggle-knob">
                      <FontAwesomeIcon icon={faPowerOff} />
                    </span>
                  </button>
                  <span className="power-toggle-state">{isDeviceOn ? 'Bypassed' : 'Normal'}</span>
                </div>

                <Modal isOpen={showPowerConfirm} toggle={() => setShowPowerConfirm(false)}>
                  <ModalHeader toggle={() => setShowPowerConfirm(false)} data-cy="devicePowerConfirmHeading">
                    Confirm Technician Bypass Mode Change
                  </ModalHeader>
                  <ModalBody>Are you sure you want to turn {isDeviceOn ? 'off' : 'on'} Technician Bypass Mode for this device?</ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={() => setShowPowerConfirm(false)}>
                      Cancel
                    </Button>
                    <Button id="jhi-confirm-power-toggle" data-cy="devicePowerConfirmButton" color="primary" onClick={confirmTogglePower}>
                      Confirm
                    </Button>
                  </ModalFooter>
                </Modal>
              </div>
            </div>

            <hr />
            <div className="justify-content-end d-flex form-footer">
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/device" replace color="info">
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              {!isReadOnly && (
                <Button
                  color="primary"
                  id="save-configuration"
                  data-testid="save-configuration"
                  data-cy="saveConfigurationButton"
                  type="submit"
                >
                  <Translate contentKey="entity.action.save">Save</Translate>
                </Button>
              )}
            </div>
          </form>
        </Col>
      </Row>
    </div>
  );
};
export default DeviceConfiguration;
